// @ts-check

import { renderCoordinateAxisOverlay } from './coordinate_axis_overlay.js?v=20260706d';
import { AppRuntime } from '../app/app_runtime.js?v=20260706d';
import { getPlanSvg, svgEl } from './plan_renderer.js?v=20260904f';
import {
  isPlanLayerVisible,
  planLayerForSystem,
  planLayerSvgAttributes,
  systemPlanLayerId
} from './plan_layer_service.js?v=20260814b';
import {
  appPerfEnd,
  appPerfStep,
  appPerfTimer
} from './render_scheduler.js?v=20260706e';
import * as SystemRegistry from '../registry/system_registry.js?v=20260904f';
import { S } from '../app/app_state.js?v=20260817c';
import { currentRenderViewportBounds } from './viewport_culling.js?v=20260901a';
import { getBomPlanSystemSelection } from '../bom/bom_plan_context.js?v=20260904c';

let system3DBoundaryPromise = null;
const DEFAULT_SYSTEM_3D_RENDER_TIMEOUT_MS = 120_000;
const MAX_SYSTEM_3D_RENDER_TIMEOUT_MS = 300_000;

/**
 * Erzeugt create2 DContext im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Hilfslogik.
 * - Kapselt Erzeugung neuer Fachobjekte mit passenden Defaultwerten, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} parentLayer - DOM-, SVG- oder Three.js-Zielcontainer für die Ausgabe.
 * @param {*} options - Optionale Steuerwerte, die Defaults oder Verhalten überlagern.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
export function create2DContext(parentLayer, options = {}) {
  const renderOptions = { ...options };
  // Cache-Steuerung bleibt beim Host: reuseFrom enthält fremde Systemlayer.
  delete renderOptions.reuseFrom;
  delete renderOptions.onlySystemId;
  delete renderOptions.systemIds;
  return {
    svg: getPlanSvg(),
    parentLayer,
    project: S.project,
    camera: S.camera2D,
    options: renderOptions
  };
}

function cachedSystemLayerNode(reuseFrom, systemId) {
  if (!reuseFrom) return null;
  for (const child of reuseFrom.children) {
    if (child.getAttribute('data-system-id') === systemId) return child;
  }
  return null;
}

// Mirrors create2DSystemTarget's own conditions for producing a
// <g data-system-id> wrapper. Lazy-manifest systems without a loaded
// render2D (most registered systems, in any given project) never get one --
// the reuse loop below must not expect a cached node for those, or it would
// always report failure and force a full rebuild for every drag.
function systemHasLayerWrapper(system) {
  if (typeof system.render2D !== 'function') return false;
  if (system?.meta?.planLayerId === false) return false;
  return Boolean(systemRenderPlanLayer(system)?.id);
}

function systemRenderPlanLayer(system) {
  const configured = planLayerForSystem(system);
  if (configured?.id) return configured;
  if (system?.meta?.planLayerId === false) return null;
  const id = systemPlanLayerId(system?.id);
  return id ? { id } : null;
}

/**
 * Rendert render Systems2 D im Bereich Kernanwendung.
 *
 * @param {*} parentLayer - DOM-, SVG- oder Three.js-Zielcontainer für die Ausgabe.
 * @param {*} options - Optionale Steuerwerte; `onlySystemId`+`reuseFrom` rendern nur
 *   das eine System frisch und haengen fuer alle anderen Systeme deren bereits
 *   gerenderten `<g data-system-id>`-Knoten aus `reuseFrom` unveraendert um
 *   (kein erneutes Klonen ihrer platzierten SVG-Assets) -- siehe
 *   plan_renderer.js's "active-system-only"-Pfad fuer Objekt-Drags.
 * @returns {boolean} `false`, wenn fuer `onlySystemId` ein anderes System
 *   keinen wiederverwendbaren Cache-Knoten hatte (Aufrufer muss dann komplett
 *   neu rendern, statt ein unvollstaendiges Ergebnis zu uebernehmen).
 */
export function renderSystems2D(parentLayer, options = {}) {
  if (!parentLayer) return true;
  const profile = appPerfTimer('render_service.renderSystems2D');
  // Computed once per render pass (not per system) so systems that opt into
  // viewport culling (see js/core/render/viewport_culling.js) don't each pay
  // for their own getCanvasSize()/screenToWorld() call. null during BOM/print
  // capture -- systems must then render every object regardless of the
  // on-screen camera framing.
  const ctx = {
    ...create2DContext(parentLayer, options),
    renderBounds: options.isBomPlan === true ? null : currentRenderViewportBounds()
  };
  const onlySystemId = options.onlySystemId || null;
  const reuseFrom = options.reuseFrom || null;
  let reuseOk = true;
  for (const system of SystemRegistry.allSystems()) {
    if (onlySystemId && system.id !== onlySystemId) {
      if (!systemHasLayerWrapper(system)) {
        appPerfStep(profile, system.id || 'system');
        continue;
      }
      const cachedNode = cachedSystemLayerNode(reuseFrom, system.id);
      if (cachedNode) {
        parentLayer.appendChild(cachedNode);
      } else {
        reuseOk = false;
      }
      appPerfStep(profile, system.id || 'system');
      continue;
    }
    if (typeof system.render2D === 'function') {
      const systemTarget = create2DSystemTarget(parentLayer, ctx, system, 'content');
      if (!systemTarget.render) {
        appPerfStep(profile, system.id || 'system');
        continue;
      }
      const systemProfile = appPerfTimer(`render_service.render2D:${system.id || 'unknown'}`);
      const outputSnapshot = captureRenderChildren([parentLayer, systemTarget.ctx?.svg]);
      try {
        system.render2D(systemTarget.ctx);
        restore2DSystemTarget(systemTarget, parentLayer, system);
      } catch (error) {
        rollbackRenderChildren(outputSnapshot);
        removeRenderNode(systemTarget.node);
        if (options.strict === true) throw error;
        reportSystemRenderError(system, '2D', error);
      } finally {
        appPerfEnd(systemProfile, 16);
      }
    }
    appPerfStep(profile, system.id || 'system');
  }
  appPerfEnd(profile);
  return reuseOk;
}

/**
 * Rendert render System Overlays2 D im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Hilfslogik.
 * - Kapselt Darstellung im Plan, Layer-Aufbau oder grafische Markierung, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} parentLayer - DOM-, SVG- oder Three.js-Zielcontainer für die Ausgabe.
 * @param {*} options - Optionale Steuerwerte, die Defaults oder Verhalten überlagern.
 * Seiteneffekte: Schreibt in DOM, SVG, Canvas oder Three.js-Strukturen und beeinflusst die sichtbare Darstellung.
 */
export function renderSystemOverlays2D(parentLayer, options = {}) {
  if (!parentLayer) return;
  const profile = appPerfTimer('render_service.renderSystemOverlays2D');
  const ctx = create2DContext(parentLayer, options);
  for (const system of SystemRegistry.allSystems()) {
    if (typeof system.render2DOverlay === 'function') {
      const systemTarget = create2DSystemTarget(parentLayer, ctx, system, 'overlay');
      if (!systemTarget.render) {
        appPerfStep(profile, system.id || 'system');
        continue;
      }
      const systemProfile = appPerfTimer(`render_service.render2DOverlay:${system.id || 'unknown'}`);
      const outputSnapshot = captureRenderChildren([parentLayer, systemTarget.ctx?.svg]);
      try {
        const output = system.render2DOverlay(systemTarget.ctx);
        if (options.gpuInteraction && (system.id === options.onlySystemId || options.systemIds?.includes(system.id))
          && output?.gpuPreview !== undefined) options.setGpuPreview?.(output.gpuPreview, system.id);
        restore2DSystemTarget(systemTarget, parentLayer, system);
      } catch (error) {
        rollbackRenderChildren(outputSnapshot);
        removeRenderNode(systemTarget.node);
        if (options.strict === true) throw error;
        reportSystemRenderError(system, '2D-Overlay', error);
      } finally {
        appPerfEnd(systemProfile, 16);
      }
    }
    appPerfStep(profile, system.id || 'system');
  }
  appPerfEnd(profile);
}

function create2DSystemTarget(parentLayer, ctx, system, role) {
  const renderIds = ctx?.options?.isBomPlan === true
    ? getBomPlanSystemSelection(system?.id)
    : null;
  const systemContext = {
    ...ctx,
    options: { ...(ctx?.options || {}), renderIds },
    renderIds,
    system
  };
  if (system?.meta?.planLayerId === false) return { render: false, ctx: systemContext, node: null };
  const layer = systemRenderPlanLayer(system);
  if (!layer?.id) return { render: false, ctx: systemContext, node: null };
  const attributes = planLayerSvgAttributes(layer.id, S.project, {
    role,
    attrs: {
      class: 'product-system-plan-layer',
      'data-system-id': system.id || 'unknown'
    }
  });
  const layerParent = svgEl('g', attributes);
  const render = isPlanLayerVisible(S.project, layer.id);
  if (!render) appendRenderNode(parentLayer, layerParent);
  return {
    render,
    node: layerParent,
    attributes,
    ctx: {
      ...systemContext,
      // Produktcode erhaelt nur seinen isolierten Systemlayer als SVG-Ziel.
      // Dadurch kann ein versehentliches ctx.svg.appendChild() den Owner-
      // Wrapper und damit die Gruppenfilter-Grenze nicht umgehen.
      svg: layerParent,
      parentLayer: layerParent,
      planLayer: layer
    }
  };
}

export async function renderSystems3D(ctx) {
  const profile = appPerfTimer('render_service.renderSystems3D');
  if (ctx && !ctx.systemRoots) ctx.systemRoots = new Map();
  try {
    const {
      commitSystem3DStagingRoot,
      createSystem3DProjectSnapshot,
      createSystem3DStagingContext,
      discardSystem3DTree
    } = await loadSystem3DBoundary();
    let projectSnapshot;
    try {
      projectSnapshot = createSystem3DProjectSnapshot(ctx?.project);
    } catch (error) {
      projectSnapshot = Object.freeze({});
      console.error('[KS Studio] Der 3D-Projektsnapshot war ungueltig und wurde leer isoliert.', error);
    }
    const timeoutMs = system3DRenderTimeoutMs(ctx?.systemRenderTimeoutMs);
    for (const system of SystemRegistry.allSystems()) {
      const render = system.render3D || system.build3D;
      if (typeof render === 'function') {
        const systemProfile = appPerfTimer(`render_service.render3D:${system.id || 'unknown'}`);
        const startedAt = nowRenderPerformanceMs();
        let stagingRoot = null;
        let committedRoot = null;
        let failed = false;
        try {
          const systemCtx = createSystem3DStagingContext(ctx, system, { projectSnapshot });
          stagingRoot = systemCtx.root;
          await waitForSystem3DRenderer(render.call(system, systemCtx), system, timeoutMs);
          committedRoot = commitSystem3DStagingRoot(stagingRoot, system);
          await discard3DSystemRoot(stagingRoot, discardSystem3DTree, { disposeResources: false });
          stagingRoot = null;
          if (committedRoot && !attach3DSystemRoot(ctx, system, committedRoot)) {
            throw new Error('Der isolierte 3D-Systemroot konnte nicht an den Host uebergeben werden.');
          }
          committedRoot = null;
        } catch (error) {
          failed = true;
          await discard3DSystemRoot(stagingRoot, discardSystem3DTree, { disposeResources: false });
          await discard3DSystemRoot(committedRoot, discardSystem3DTree, { disposeResources: true });
          reportSystemRenderError(system, '3D', error);
        } finally {
          ctx?.performanceStats?.systems?.push({
            id: system.id || 'unknown',
            label: system.label || system.name || system.id || 'System',
            durationMs: nowRenderPerformanceMs() - startedAt,
            ...(failed ? { failed: true } : {})
          });
          appPerfEnd(systemProfile, 16);
        }
      }
      appPerfStep(profile, system.id || 'system');
    }
  } finally {
    appPerfEnd(profile);
  }
}

function attach3DSystemRoot(ctx, system, root) {
  if (!ctx?.root || root === ctx.root || !root?.children?.length) return false;
  ctx.root.add(root);
  const attached = root.parent === ctx.root || ctx.root.children?.includes?.(root) === true;
  if (!attached) return false;
  const systemId = system.id || 'unknown';
  if (ctx.systemRoots instanceof Map) {
    ctx.systemRoots.set(systemId, root);
  }
  return true;
}

function restore2DSystemTarget(target, parentLayer, system) {
  const node = target?.node;
  if (!node || typeof node.setAttribute !== 'function') return;
  if (node.parentNode !== parentLayer) {
    removeRenderNode(node);
    appendRenderNode(parentLayer, node);
  }
  const attributes = {
    ...(target?.attributes || {}),
    class: 'product-system-plan-layer',
    'data-system-id': system?.id || 'unknown'
  };
  for (const name of [
    'class',
    'data-system-id',
    'data-plan-layer-id',
    'data-plan-layer-group-id',
    'data-plan-layer-role',
    'data-plan-layer-visible',
    'display'
  ]) {
    if (Object.prototype.hasOwnProperty.call(attributes, name)) {
      node.setAttribute(name, attributes[name]);
    } else {
      node.removeAttribute?.(name);
    }
  }
}

function captureRenderChildren(containers = []) {
  const snapshots = new Map();
  for (const container of containers) {
    if (!container?.children || snapshots.has(container)) continue;
    snapshots.set(container, Array.from(container.children));
  }
  return snapshots;
}

function rollbackRenderChildren(snapshots) {
  snapshots?.forEach?.((snapshotChildren, container) => {
    const childrenBefore = Array.isArray(snapshotChildren) ? snapshotChildren : [];
    const expected = new Set(childrenBefore);
    for (const child of Array.from(container?.children || [])) {
      if (!expected.has(child)) removeRenderNode(child, container);
    }
    // Auch entfernte oder umgehaengte vorherige Ausgaben werden wieder an
    // denselben Container und in dieselbe Reihenfolge gesetzt.
    for (const child of childrenBefore) {
      removeRenderNode(child);
      appendRenderNode(container, child);
    }
  });
}

function appendRenderNode(container, node) {
  if (!container || !node) return;
  if (typeof container.appendChild === 'function') {
    container.appendChild(node);
    return;
  }
  if (typeof container.add === 'function') container.add(node);
}

function removeRenderNode(node, explicitParent = null) {
  if (!node) return;
  const parent = explicitParent || node.parentNode || node.parent || null;
  if (typeof parent?.removeChild === 'function') {
    parent.removeChild(node);
    return;
  }
  if (typeof parent?.remove === 'function') {
    parent.remove(node);
    return;
  }
  if ('parentNode' in Object(node)) node.remove?.();
}

function loadSystem3DBoundary() {
  if (!system3DBoundaryPromise) {
    system3DBoundaryPromise = import('../three/system_3d_boundary.mjs?v=20260904f').catch(error => {
      system3DBoundaryPromise = null;
      throw error;
    });
  }
  return system3DBoundaryPromise;
}

async function discard3DSystemRoot(root, discardSystem3DTree, options = {}) {
  if (!root) return;
  let disposeObjectTree = null;
  if (options.disposeResources === true) {
    try {
      ({ disposeObjectTree } = await import('../three/three_asset_loader.js?v=20260706d'));
    } catch {
      // Der Baum wird auch ohne optionale Dispose-Runtime sicher geloest.
    }
  }
  try {
    await discardSystem3DTree(root, {
      disposeObjectTree,
      disposeResources: options.disposeResources === true
    });
  } catch {
    // Ein Cleanup-Fehler darf weder den urspruenglichen Rendererfehler
    // verdecken noch den naechsten unabhaengigen Systemrenderer blockieren.
  }
}

function system3DRenderTimeoutMs(value) {
  const requested = Number(value);
  if (!Number.isFinite(requested) || requested <= 0) return DEFAULT_SYSTEM_3D_RENDER_TIMEOUT_MS;
  return Math.min(MAX_SYSTEM_3D_RENDER_TIMEOUT_MS, Math.max(1, Math.round(requested)));
}

async function waitForSystem3DRenderer(result, system, timeoutMs) {
  let timer = null;
  try {
    await Promise.race([
      Promise.resolve(result),
      new Promise((_, reject) => {
        timer = setTimeout(() => {
          const error = new Error(`3D-Renderer von "${system?.id || 'unknown'}" hat das Zeitlimit ueberschritten.`);
          error.code = 'SYSTEM_3D_RENDER_TIMEOUT';
          reject(error);
        }, timeoutMs);
      })
    ]);
  } finally {
    if (timer !== null) clearTimeout(timer);
  }
}

function reportSystemRenderError(system, phase, error) {
  const systemId = system?.id || 'unknown';
  console.error(`[KS Studio] ${phase}-Renderer von System "${systemId}" fehlgeschlagen.`, error);
}

function nowRenderPerformanceMs() {
  return Number(globalThis.performance?.now?.()) || Date.now();
}

/**
 * Rendert draw Global Overlays im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Hilfslogik.
 * - Kapselt Darstellung im Plan, Layer-Aufbau oder grafische Markierung, damit Aufrufer nicht die Moduldetails kennen müssen.
 * Seiteneffekte: Schreibt in DOM, SVG, Canvas oder Three.js-Strukturen und beeinflusst die sichtbare Darstellung.
 */
export function drawGlobalOverlays() {
  renderCoordinateAxisOverlay();
  AppRuntime.drawPlanInteractionOverlay();
}
